/**
 * A beacon over a loose part of an assembly (design D2): a thin amber column, `#ffb85c`
 * (design D3 — the gate's glow and the dog's eyes are `#7fe8ff`, so a beacon can never be
 * mistaken for the way out), rising `BEACON_HEIGHT` above the part it marks.
 *
 * Rendered as a child of the part's own `<RigidBody>` rather than tracked in world space:
 * it goes where the part goes for free, with no position bookkeeping — a leg thrown down a
 * corridor is marked where it lands. What being a child does *not* get for free is the
 * direction: a part that landed on its side would rake its beacon along the ground, where
 * it clears no hedge and marks nothing. So the one thing the beacon does per frame is
 * undo its parent's rotation (`uprightBeacon`) and point at the sky.
 *
 * `meshBasicMaterial` with both shadow flags off, so five more meshes cost nothing against
 * the lighting budget (no new light, no shadow-casting geometry).
 */

import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { Mesh, Quaternion, Vector3 } from 'three';

const BEACON_COLOR = '#ffb85c';

/** Tall enough to clear the hedge (3 m default) with room to read at night. */
export const BEACON_HEIGHT = 6;

const BEACON_RADIUS = 0.03;

/** Scratch, module-level: this runs every frame and must not allocate. */
const uprightRotation = new Quaternion();
const uprightOffset = new Vector3();

/**
 * Point `beacon` straight up in world space and lift it half a column above the part it
 * hangs under, whatever that part is doing.
 *
 * Both halves are needed: the rotation is the parent's, inverted, and the offset has to be
 * expressed in the parent's turned frame or the column would rise sideways from the part
 * instead of out of it. A mesh with no parent — mid-mount, or in a test — is left alone.
 */
export function uprightBeacon(beacon: Mesh): void {
  const part = beacon.parent;
  if (!part) return;

  part.getWorldQuaternion(uprightRotation).invert();
  beacon.quaternion.copy(uprightRotation);
  beacon.position
    .copy(uprightOffset.set(0, BEACON_HEIGHT / 2, 0))
    .applyQuaternion(uprightRotation);
}

export function PartBeacon() {
  const beaconRef = useRef<Mesh>(null);

  // Cheap enough to leave on: a quaternion inverse and a vector rotation per loose part,
  // and a level holds five. Nothing here touches React or the store — the render loop
  // reads a transform and writes a transform (CLAUDE.md, performance).
  useFrame(() => {
    if (beaconRef.current) uprightBeacon(beaconRef.current);
  });

  return (
    <mesh
      ref={beaconRef}
      name="part-beacon"
      position={[0, BEACON_HEIGHT / 2, 0]}
      castShadow={false}
      receiveShadow={false}
    >
      <cylinderGeometry args={[BEACON_RADIUS, BEACON_RADIUS, BEACON_HEIGHT, 6]} />
      <meshBasicMaterial color={BEACON_COLOR} />
    </mesh>
  );
}
